@if (hasClientRecord()) {
  <iris-card>
    <section class="docs-view-header" data-dd-privacy="allow">
      <div class="docs-info-wrapper">
        <h2>From SonderMind</h2>
        <div>
          <p class="send-docs-text">
            {{ sendDocsText() }}
          </p>
        </div>
      </div>
      @if (!isSaasEnabled()) {
        <div class="send-docs-wrapper">
          <iris-button
            class="send-docs-button"
            (click)="sendDocs()"
            data-test="button-send-docs"
          >
            Send documents
          </iris-button>
        </div>
      }
    </section>
    @if (docs(); as docs) {
      @if (docs.length > 0) {
        <div class="docs-table-wrapper">
          <table class="docs-table">
            <thead>
              <tr>
                <th class="col-title">Document name</th>
                <th class="col-sent-date">Date sent</th>
                <th class="col-status">Status</th>
                <th class="col-download">Download</th>
              </tr>
            </thead>
            <tbody>
              @for (row of docs; track row.title) {
                <tr data-test="client-doc-row">
                  <td class="col-title" data-test="client-doc-item">
                    @if (row.title) {
                      <span class="title-row">{{ row.title }}</span>
                    }
                  </td>
                  <td class="col-sent-date">
                    @if (row.sentDate) {
                      <span>{{ row.sentDate | localizedDate:'short' }}</span>
                    }
                  </td>
                  <td class="col-status" data-test="client-doc-row-status">
                    @if (row.status) {
                      <span>
                        @if (row.status === documentStatus.COMPLETED) {
                          <iris-icon name="check" class="check-icon"></iris-icon>
                          {{ row.statusCopy }} - {{ row.completedDate | localizedDate:'short' }}
                        } @else {
                          {{ row.statusCopy }}
                        }
                      </span>
                    }
                  </td>
                  <td class="col-download">
                    @if (row.status) {
                      @if (row.downloadUrl) {
                        <iris-icon-button
                          icon="download"
                          ariaLabel="Download document"
                          [linkHref]="row.downloadUrl"
                          [target]="irisButtonTarget.BLANK"
                          [type]="irisIconButtonTypes.TERTIARY"
                          data-test="docs-item-download"
                        ></iris-icon-button>
                      } @else {
                        <iris-icon-button
                          icon="download"
                          ariaLabel="Download unavailable"
                          [disabled]="true"
                          [type]="irisIconButtonTypes.TERTIARY"
                          data-test="docs-item-download-disabled"
                        ></iris-icon-button>
                      }
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <section class="no-docs-section">
          @if (!loading()) {
            <img src="/assets/documents/docs-stack.svg" alt="">
            <h2 class="iris-h2-responsive">No documents sent through SonderMind</h2>
            <div class="no-docs-instructions">
              @if (isSaasEnabled()) {
                <p class="iris-body-responsive">
                  Make sure all clients complete the required documents. Any documents you send to them will appear here.
                </p>
              } @else {
                <p class="iris-body-responsive">
                  Make sure all clients complete the required documents—either through SonderMind, or on your own. Send your first document or
                  <a
                    href="{{ docDisclosureInfo }}"
                    target="_blank"
                    data-test="doc-disclosures-info"
                  >
                    learn more about required documents for your state.
                  </a>
                </p>
              }
            </div>
          }
        </section>
      }
    }
  </iris-card>

  @if (displayFileUploadHistory()) {
    <iris-card>
      <section class="docs-view-header" data-dd-privacy="allow">
        <div class="docs-info-wrapper">
          <h2 data-test="shared-via-chat-title">Shared via chat</h2>
          <p class="send-docs-text">These are the documents you've uploaded and shared with your client</p>
        </div>
      </section>
      @if (sharedFilesState().loading) {
        <div class="shared-files-loading">
          <iris-spinner size="md"></iris-spinner>
        </div>
      } @else if (sharedFilesState().files.length > 0) {
        <div class="docs-table-wrapper">
          <table class="docs-table">
            <thead>
              <tr>
                <th class="col-title">Document name</th>
                <th class="col-sent-date">Date sent</th>
                <th class="col-status">Hidden</th>
                <th class="col-download">Download</th>
              </tr>
            </thead>
            <tbody>
              @for (file of sharedFilesState().files; track file.uuid) {
                <tr data-test="shared-file-row">
                  <td class="col-title" data-test="shared-file-name">
                    <span class="title-row">{{ file.name }}</span>
                  </td>
                  <td class="col-sent-date">
                    <span>{{ file.createdAt | localizedDate:'short' }}</span>
                  </td>
                  <td class="col-status">
                    <iris-icon-button
                      [icon]="file.hidden ? 'visibility_off' : 'visibility'"
                      [ariaLabel]="file.hidden ? 'Show file' : 'Hide file'"
                      (click)="openHideConfirmationModal(file)"
                      [disabled]="togglingFileUuid() === file.uuid"
                      [type]="irisIconButtonTypes.TERTIARY"
                      data-test="toggle-file-visibility"
                    ></iris-icon-button>
                    @if (togglingFileUuid() === file.uuid) {
                      <iris-spinner size="sm"></iris-spinner>
                    }
                  </td>
                  <td class="col-download">
                    <iris-icon-button
                      icon="download"
                      [ariaLabel]="file.hidden ? 'Download unavailable' : 'Download document'"
                      (click)="downloadSharedFile(file)"
                      [disabled]="file.hidden || togglingFileUuid() === file.uuid"
                      [type]="irisIconButtonTypes.TERTIARY"
                      data-test="shared-file-download"
                    ></iris-icon-button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (sharedFilesState().totalPages > 1) {
          <sondermind-pagination
            [totalItems]="sharedFilesState().totalCount"
            [itemsPerPage]="sharedFilesState().pageSize"
            [currentPage]="sharedFilesState().currentPage"
            (pageChanged)="onSharedFilesPageChange($event)"
            data-test="shared-files-paginator"
          ></sondermind-pagination>
        }
      } @else if (sharedFilesState().totalCount === 0) {
        <section class="no-docs-section">
          <p class="iris-body-responsive">No files uploaded yet.</p>
        </section>
      }
    </iris-card>
  }
} @else {
  @if (!loading()) {
    <section class="docs-view-header">
      <div class="docs-info-wrapper">
        <h2>CLIENT DOCUMENTS</h2>
        <div class="no-account-info-container">
          <iris-icon
            name="info"
            [color]="iconColors.WARNING"
            [size]="iconSizes.SM"
            [usage]="iconUsageTypes.DECORATIVE"
          ></iris-icon>
          <p class="no-account-info-text">
            {{ noAccountInfoText() }}
          </p>
        </div>
      </div>
    </section>
  }
}

@if (vaRfsEnabled()) {
  <iris-card>
    <section class="docs-view-header" data-dd-privacy="allow">
      <div class="docs-info-wrapper">
        <h2 data-test="rfs-submissions-title">{{ rfsCopy.listSectionHeader }}</h2>
        <p class="send-docs-text">{{ rfsCopy.listSectionSubtext }}</p>
      </div>
    </section>
    @if (rfsLoading()) {
      <div class="shared-files-loading">
        <iris-spinner size="md"></iris-spinner>
      </div>
    } @else if (rfsLoadError()) {
      <section class="no-docs-section" role="alert">
        <p class="iris-body-responsive">{{ rfsCopy.listError }}</p>
      </section>
    } @else if (rfsRows().length > 0) {
      <div class="docs-table-wrapper">
        <table class="docs-table">
          <thead>
            <tr>
              <th class="col-title">{{ rfsCopy.listColDocument }}</th>
              <th class="col-status">{{ rfsCopy.listColStatus }}</th>
              <th class="col-sent-date">{{ rfsCopy.listColDate }}</th>
              <th class="col-download"></th>
            </tr>
          </thead>
          <tbody>
            @for (row of rfsRows(); track row.id) {
              <tr data-test="rfs-submission-row">
                <td class="col-title">
                  <span class="title-row rfs-doc-name">{{ rfsCopy.listDocumentName }}</span>
                  <span class="rfs-doc-sub">{{ row.authSubline }}</span>
                </td>
                <td class="col-status">
                  @if (row.hasStatusTooltip) {
                    <!-- Plain text can't take the focus iris-tooltip opens on, hence the tabindex. -->
                    <iris-tooltip
                      #statusTooltip
                      class="rfs-status-tooltip"
                      [tooltipText]="rfsCopy.submittedToVaTooltip"
                      [duration]="0"
                      data-test="rfs-status-tooltip"
                    >
                      <span
                        class="rfs-status-with-tooltip"
                        data-test="rfs-status"
                        tabindex="0"
                        [attr.aria-describedby]="statusTooltip.tooltipId"
                      >{{ row.statusLabel }}</span>
                    </iris-tooltip>
                  } @else {
                    <span data-test="rfs-status">{{ row.statusLabel }}</span>
                  }
                </td>
                <td class="col-sent-date">
                  @if (row.submittedAt) {
                    <span>{{ row.submittedAt | localizedDate:'short' }}</span>
                  } @else {
                    <span class="rfs-dash">—</span>
                  }
                </td>
                <td class="col-download">
                  @if (row.canResume) {
                    <iris-button
                      class="rfs-resume-button"
                      [buttonType]="rfsResumeButtonType"
                      (click)="onResumeRfs(row)"
                      data-test="rfs-resume"
                    >
                      {{ rfsCopy.listActionResume }}
                    </iris-button>
                  } @else if (row.canRetry) {
                    <iris-button
                      class="rfs-resume-button"
                      [buttonType]="rfsResumeButtonType"
                      (click)="onRetryRfs(row)"
                      data-test="rfs-retry"
                    >
                      {{ rfsCopy.listActionRetry }}
                    </iris-button>
                  } @else if (row.canDownload) {
                    @if (isDownloadingRfs(row)) {
                      <iris-spinner
                        size="sm"
                        [removeTopMargin]="true"
                        data-test="rfs-download-loading"
                      ></iris-spinner>
                    } @else {
                      <iris-icon-button
                        icon="download"
                        ariaLabel="Download form"
                        (click)="onDownloadRfs(row)"
                        [type]="irisIconButtonTypes.TERTIARY"
                        data-test="rfs-download"
                      ></iris-icon-button>
                    }
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <section class="no-docs-section">
        <p class="iris-body-responsive">{{ rfsCopy.listEmpty }}</p>
      </section>
    }
  </iris-card>
}
